/**
 * FkLookupPicker — Dialog that lets the user pick a row from a foreign-key
 * parent table. Used by filter rows whose operator is `lookup`.
 *
 * On selection, returns the value of the FK target column for the chosen row
 * back to the caller via `onSelect`. All filtering (the main search, plus any
 * filters configured via the inline FilterWizard) is sent to the database —
 * this is not a client-side filter.
 *
 * The main search box matches both the parent table's display column and the
 * FK target column. Because the existing filter model AND-joins groups (and
 * has no cross-column OR), we run **two parallel queries** when both apply
 * and merge the results client-side, deduplicating by FK value and capping at
 * `PICKER_ROW_LIMIT`. Each individual query is still server-evaluated.
 *
 * Below the search bar we mount the project's regular `FilterWizard` so the
 * full filter UX — chip operators, JSON sub-operators, FK drill-down via
 * `lookup`, peek-matching-rows — is available without duplicating the column-
 * header filter implementation.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  X,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Link2,
  ChevronRight,
  Pencil,
  Filter,
  Plus,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverPortalDefaultProvider,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { fetchTableExplorerRows } from '../services/tableExplorerApi';
import { createExecutionId } from '@/types/queryExecution';
import { pickDisplayColumn } from '../utils/pickDisplayColumn';
import { resolveTableDisplayColumn } from '../../common/utils/tableDisplayColumn';
import { useTableMetadata } from '../hooks/useTableMetadata';
import { useAvailableTables } from '../hooks/useAvailableTables';
import { searchHostPickerRows, useFkPickerHostSource } from '../hooks/useFkPickerHostSource';
import type { TableExplorerRelationshipPort } from './tableExplorerHostPorts';
import { categorizeDataType } from '../../common/utils/filterUtils';
import {
  categorizeJsonColumn,
  type JsonColumnCategory,
} from '../../common/utils/jsonColumnCategorization';
import {
  categorizeEnumColumn,
  type EnumColumnCategory,
} from '../../common/utils/enumColumnCategorization';
import { JsonHeaderGlyph } from '../../common/components/grid-glyphs/JsonHeaderGlyph';
import { EnumHeaderGlyph } from '../../common/components/grid-glyphs/EnumHeaderGlyph';
import { LooseRelationshipTargetPicker } from '../../common/components/filters/LooseRelationshipTargetPicker';
import { LooseRelationshipEditorDialog } from '../../common/components/filters/LooseRelationshipEditorDialog';
import { FilterWizard } from '../../common/components/filters/FilterWizard';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';
import { isEscapeClaimTarget } from '../../common/utils/escapeClaimTarget';
import type { InferTable } from '../../common/utils/inferLooseRelationship';
import { useAnalytics } from '../../../../core/analytics';
import { useMetaInfo, useInvalidateMetaInfo } from '../../common/hooks/useMetaInfo';
import { removeLooseRelationship } from '../../common/services/metaInfoApi';
import {
  findLooseRelationshipsForColumn,
  qualifiedLabel,
  resolveActiveLooseRel,
  resolveActiveTargetFilter,
} from '../../common/utils/looseFkResolver';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  FilterGroup,
  FilterCondition,
  TableExplorerColumn,
  SortDefinition,
} from '../../core/types/workspaceTypes';

const PICKER_ROW_LIMIT = 100;
const DEBOUNCE_MS = 250;

/**
 * Breadcrumb context describing where this picker sits in a drill-down chain.
 * Rendered above the dialog title so the user can see the trail like
 *   `Orders ▸ customer_id ▸ Customers ▸ region_id ▸ Regions`
 * The chain conveys "we're picking a value to fill in {childColumn} of {childTable}".
 */
type FkLookupPickerCrumb = {
  /** Display name of the table we drilled FROM (the parent picker's table). */
  fromTable: string;
  /** Column we're filling in on that table. */
  fromColumn: string;
};

type FkLookupPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  database: string;
  /** Parent (referenced) schema. May be empty when no relationship is declared yet. */
  referencedSchema: string;
  /** Parent (referenced) table name. Empty string means no target — the picker
   *  shows the loose-FK target selection UI instead of the row grid. */
  referencedTable: string;
  /** Column on the parent table that the FK points at — its value is returned.
   *  Empty when no target is declared yet. */
  referencedColumn: string;
  /** Title used in the dialog header (typically "Pick a {sourceColumn}"). */
  sourceLabel?: string;
  /**
   * Source-side context — required to render the target-selection top section
   * when the picker is opened on a non-FK column with no declared loose-FK.
   * Optional only because legacy real-FK callers don't need it.
   */
  source?: {
    schema: string;
    table: string;
    column: string;
    dataType: string;
  };
  /**
   * Drill-down breadcrumb chain. Empty for the top-level picker; populated
   * by nested pickers so the user can see how they got here. Each crumb is
   * pushed when the user clicks the "drill into FK" icon next to a column
   * filter input.
   */
  crumbs?: FkLookupPickerCrumb[];
  /**
   * Optional target-filter from a loose FK's targetFilter field.
   * When present, narrows the picker's row queries to match the loose FK scope.
   */
  targetFilter?: FilterGroup[];
  /**
   * Engine of the connection — needed so the per-column filter operator
   * dropdown in the picker grid is JSON-aware. When omitted (legacy callers),
   * the picker falls back to the non-JSON-aware operator list.
   */
  engine?: DatabaseEngineId;
  /**
   * Active JSON `sourcePath` for the loose-FK declaration being operated on.
   * When set, the picker resolves the matching `LooseRelationship` from the
   * set of declarations on `source.{schema,table,column}` so columns hosting
   * multiple loose FKs (e.g. `$.customer_id` + `$.warehouse_id`) work
   * independently. When omitted and the source column has a single
   * declaration, that one wins (Pass-2 compatible). When omitted and more
   * than one declaration exists, the picker shows a chooser.
   */
  sourcePath?: string;
  /**
   * Explicit pre-selection of a loose-FK declaration by id. When set, seeds
   * the picker's `chosenLooseRelId` so the multi-FK chooser is skipped and
   * the row grid opens directly against that declaration's target. Use this
   * when the caller has already disambiguated the loose rel (e.g. cell-edit
   * popover let the user pick which target to search). Takes precedence over
   * `sourcePath` for resolving `activeLooseRel`.
   */
  preselectedLooseRelId?: string;
  /**
   * True when `referenced*` come from a REAL FK constraint on `source`
   * (`hasRealForeignKeyTarget`). A real FK wins over any loose declaration on
   * the same column — Table Explorer's `buildForeignKeyMap` and the VQE column
   * adapter apply that precedence — so the picker then reads ONLY the props:
   * the constraint's target, the caller's `targetFilter`, and no chooser,
   * "edit target" or remove affordance for a declaration it would otherwise
   * resolve from MetaInfo.
   */
  referencesRealForeignKey?: boolean;
  /**
   * Called when the user picks a row. The optional second argument carries
   * the `sourcePath` of the loose-FK declaration the picker resolved to —
   * either from the caller's `sourcePath` prop (single-FK or pre-bound) or
   * from the multi-FK chooser the user just navigated. Hosts that drive a
   * loose-FK filter condition (Pass 3 Slice A's deferred write-back, plus
   * Slice C's `*_lookup` sub-ops) use this to stamp `condition.sourcePath`
   * with the user's actual pick instead of a "find-first-match" fallback.
   * `undefined` means "no path bound" (real FK, or no declaration matched).
   */
  onSelect: (
    value: string | number | boolean | Date,
    sourcePath?: string,
  ) => void;
  /**
   * Optional "Clear" affordance — when provided, the dialog footer renders
   * a "Clear" button next to "Cancel". Clicking it fires `onClear()` and
   * closes the dialog. Hosts wire this to null/empty their stored value
   * (FK column null, cell value null, etc.). Omit to hide the button when
   * clearing isn't meaningful for the caller's context.
   */
  onClear?: () => void;
  /**
   * Host relationship / metadata port (Azure Table, S4 review C, M7): tables,
   * target columns and candidate rows come from it — the picker then never
   * calls `useAvailableTables`, `useTableMetadata` or `fetchTableExplorerRows`
   * — and it searches / sorts the host page locally. The SQL-only inline
   * filter wizard (whose chips can open nested SQL pickers) is not offered.
   * Absent = every SQL engine, unchanged.
   */
  relationshipPort?: TableExplorerRelationshipPort;
  /**
   * Element that opened the picker — focus returns to it when the dialog
   * closes. Every caller opens the picker from state (there is no
   * `DialogTrigger`), so without this Radix drops focus on `<body>`.
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
};

export function FkLookupPicker({
  open,
  onOpenChange,
  connectionId,
  database,
  referencedSchema,
  referencedTable,
  referencedColumn,
  sourceLabel,
  source,
  crumbs,
  targetFilter,
  engine,
  sourcePath,
  preselectedLooseRelId,
  referencesRealForeignKey = false,
  onSelect,
  onClear,
  relationshipPort,
  returnFocusRef,
}: FkLookupPickerProps) {
  // True while this picker is mounted AND open. Its dialogs also "close" when
  // the picker swaps one for another (row grid ↔ relationship editor), and
  // those swaps must not hand focus back to the opener — a cell editor's
  // input reopens its popover on focus, underneath the replacement dialog.
  const pickerLiveRef = useRef(false);
  useEffect(() => {
    pickerLiveRef.current = open;
    return () => {
      pickerLiveRef.current = false;
    };
  }, [open]);
  // Radix Dialog's own close handler focuses its (absent) trigger ref; hand
  // focus back to the caller's opener instead — once the whole picker has
  // closed, and only while the opener is still mounted. Radix runs this in a
  // setTimeout after the dialog content unmounts, so the effect cleanup
  // above has already run when the picker itself went away.
  const handleCloseAutoFocus = useCallback(
    (e: Event) => {
      if (pickerLiveRef.current) {
        // Internal swap: the replacing dialog already placed focus.
        e.preventDefault();
        return;
      }
      const opener = returnFocusRef?.current;
      if (!opener?.isConnected) return;
      e.preventDefault();
      opener.focus();
    },
    [returnFocusRef],
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Filters configured inside the picker via the inline FilterWizard. AND-ed
  // with the main search filter and any caller-supplied `targetFilter`.
  const [wizardFilters, setWizardFilters] = useState<FilterGroup[]>([]);
  // Column the user just picked in the toolbar "Add Filter" popover —
  // forwarded to FilterWizard as `initialDraftColumn` so the wizard mounts
  // with an empty draft on that column. Cleared via `onDraftHandled`.
  const [pendingDraftColumn, setPendingDraftColumn] = useState<string | null>(null);
  // Single-column sort applied to the picker grid. Click cycles
  // none → asc → desc → none on the column header.
  const [pickerSort, setPickerSort] = useState<SortDefinition | null>(null);
  // Row the user has highlighted in the grid. A single click selects;
  // committing requires the explicit "Apply" footer button (or a
  // double-click shortcut). Stored as the row's effective-column value
  // (the value the picker would return) so it's stable across re-renders
  // and survives mergedRows reshuffling between query refetches.
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState(false);
  // When N > 1 loose FKs are declared on the source column and the caller
  // didn't pass a `sourcePath` (or `preselectedLooseRelId`), we render a
  // chooser. The user's pick is tracked locally so the chooser collapses to
  // the row-grid view for the chosen declaration without losing the others.
  // `preselectedLooseRelId` lets callers (e.g. the cell-edit popover that has
  // already let the user disambiguate) skip the chooser entirely.
  const [chosenLooseRelId, setChosenLooseRelId] = useState<string | null>(
    preselectedLooseRelId ?? null,
  );
  // "+ Add another" from the chooser opens the LooseRelationshipTargetPicker
  // as an explicit add flow — distinct from `editingTarget` which edits an
  // existing declaration.
  const [addingAnother, setAddingAnother] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { track } = useAnalytics();
  const invalidate = useInvalidateMetaInfo();

  // Loose-FK lookup for the current source column. All declarations sharing
  // the source `(schema, table, column)` are surfaced — a JSON column can host
  // multiple loose FKs distinguished by `sourcePath` (Pass 3, Slice A). The
  // active one is resolved by `sourcePath`; when none was passed and the
  // column has multiple declarations, the chooser surfaces instead of the
  // row grid so the user can disambiguate. A real FK constraint on the column
  // wins over every declaration (`referencesRealForeignKey`), so none is
  // surfaced — the target, filter and returned value follow the constraint.
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const looseRels = useMemo(() => {
    if (!source || referencesRealForeignKey) return [];
    return findLooseRelationshipsForColumn(
      metaInfo?.looseRelationships,
      source.schema,
      source.table,
      source.column,
    );
  }, [metaInfo, source, referencesRealForeignKey]);
  const activeLooseRel = useMemo(() => {
    if (chosenLooseRelId) {
      return looseRels.find((r) => r.id === chosenLooseRelId) ?? null;
    }
    return resolveActiveLooseRel(looseRels, sourcePath);
  }, [looseRels, sourcePath, chosenLooseRelId]);
  const needsChooser =
    !!source && !activeLooseRel && looseRels.length > 1 && !addingAnother;
  const isLoose = !!activeLooseRel;

  // When an `activeLooseRel` resolves to a different target than what the
  // caller passed (e.g. caller used the column's first FK from
  // useTableMetadata, but the path-keyed lookup picked a sibling), prefer
  // the path-keyed answer so the picker fetches rows from the correct
  // parent table. Fallback to props for real-FK callers where activeLooseRel
  // is null by construction.
  const effectiveSchema = activeLooseRel?.toSchema ?? referencedSchema;
  const effectiveTable = activeLooseRel?.toTable ?? referencedTable;
  const effectiveColumn = activeLooseRel?.toColumn ?? referencedColumn;
  // The target filter follows the SAME active declaration as the target
  // above — switching relationships in the chooser switches the filter, and a
  // declaration without one never inherits another relationship's.
  const effectiveTargetFilter = resolveActiveTargetFilter(activeLooseRel, targetFilter);
  const targetResolved = Boolean(effectiveTable && effectiveColumn) && !needsChooser;

  // Reset edit-mode AND chooser state whenever the dialog closes so reopening
  // always lands on the grid view (or the chooser, if N > 1 and no path).
  // `preselectedLooseRelId` is re-applied on close so the next open seeds the
  // chosen rel exactly as the caller specified.
  useEffect(() => {
    if (!open) {
      setEditingTarget(false);
      setChosenLooseRelId(preselectedLooseRelId ?? null);
      setAddingAnother(false);
    }
  }, [open, preselectedLooseRelId]);

  const onRemoveLoose = useCallback(async () => {
    if (!activeLooseRel) return;
    const confirmed = window.confirm(
      "Remove loose-FK declaration for this column? This won't delete data — only the relationship.",
    );
    if (!confirmed) return;
    await removeLooseRelationship(connectionId, database, activeLooseRel.id);
    await invalidate(connectionId, database);
    track('feature:loose_fk_removed', { source: 'inline' });
    onOpenChange(false);
  }, [activeLooseRel, connectionId, database, invalidate, track, onOpenChange]);

  // Fetch the database's tables so the loose-FK target picker can offer
  // schema.table options and feed the inference heuristic. Enabled when we
  // either need to declare a new target (no target yet) or are editing an
  // existing loose-FK target. Real-FK callers (no `source`) skip this fetch.
  const needTables = open && !!source && (!targetResolved || editingTarget || addingAnother);
  const availableTablesQuery = useAvailableTables({
    connectionId,
    database,
    enabled: needTables && !relationshipPort,
  });
  const host = useFkPickerHostSource({
    port: relationshipPort,
    open,
    connectionId,
    database,
    schema: effectiveSchema,
    table: effectiveTable,
    targetResolved,
    needTables,
    targetFilter,
  });

  const allTables = useMemo<InferTable[]>(() => {
    const tables = (relationshipPort ? host.tables : availableTablesQuery.data) ?? [];
    // We don't have per-table column lists at this level — pass empty columns
    // and let the target picker lazy-fetch metadata for the selected table.
    return tables.map((t) => ({
      schema: t.schema,
      name: t.name,
      columns: [],
    }));
  }, [availableTablesQuery.data, host.tables, relationshipPort]);

  // Track when the picker opens against an unresolved loose-FK target.
  useEffect(() => {
    if (open && !targetResolved && source) {
      track('feature:filter_operator_lookup_opened', {
        source: 'main',
        isLoose: false,
      });
    }
  }, [open, targetResolved, source, track]);

  // Reset state every time the dialog reopens. Initial focus is NOT set here:
  // a deferred `focus()` fires after the user may already be interacting
  // (e.g. opened the Add Filter popover on a warm-cache grid) and yanks focus
  // back to the search input, which dismisses that popover as focus-outside.
  // The search input gets focus synchronously via `onOpenAutoFocus` below.
  useEffect(() => {
    if (open) {
      setSearch('');
      setDebouncedSearch('');
      setWizardFilters([]);
      setPendingDraftColumn(null);
      setPickerSort(null);
      setSelectedRowKey(null);
    }
  }, [open]);

  // Cycle sort for a column: none → asc → desc → none.
  const cycleSort = useCallback((col: string) => {
    setPickerSort((prev) => {
      if (!prev || prev.field !== col) return { field: col, direction: 'asc' };
      if (prev.direction === 'asc') return { field: col, direction: 'desc' };
      return null;
    });
  }, []);

  // Stable array shape for the query payload — `[]` when no sort is set.
  const sortingForQuery = useMemo<SortDefinition[]>(
    () => (pickerSort ? [pickerSort] : []),
    [pickerSort],
  );

  // Debounce search → debouncedSearch
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fullTableName = effectiveSchema
    ? `${effectiveSchema}.${effectiveTable}`
    : effectiveTable;

  // Parent table column metadata — column names, data types, FK/PK flags.
  // Drives the display-column heuristic and per-column filter coercion.
  const sqlMetadata = useTableMetadata({
    connectionId,
    database,
    schema: effectiveSchema,
    tableName: effectiveTable,
    enabled: open && targetResolved && !relationshipPort,
  });
  const parentColumns = relationshipPort ? host.columns : sqlMetadata.allColumns;
  const primaryKeyColumns = relationshipPort ? host.primaryKeyColumns : sqlMetadata.primaryKeyColumns;
  const metadataLoading = relationshipPort ? host.columnsLoading : sqlMetadata.isLoading;
  const metadataError = relationshipPort ? host.columnsError : sqlMetadata.error;

  // Persisted "label column" (stored at the table level in MetaInfo via the
  // declare/edit picker) wins over the name heuristic. `resolveTableDisplayColumn`
  // returns `undefined` when no declaration exists for the target table, so we
  // fall back to `pickDisplayColumn` in that case. An explicit `null` means
  // the user declared "(none)" — we honour it and skip the heuristic.
  const displayColumn = useMemo(() => {
    const columnNames = parentColumns.map((c) => c.name);
    const resolved = resolveTableDisplayColumn(
      metaInfo ?? null,
      effectiveSchema,
      effectiveTable,
      columnNames,
    );
    if (resolved !== undefined) return resolved;
    return pickDisplayColumn(columnNames, effectiveColumn);
  }, [metaInfo, effectiveSchema, effectiveTable, parentColumns, effectiveColumn]);

  // Parent table's primary key (single-column only — composite PKs fall back
  // to the FK target column for search/labeling purposes). What users
  // typically think of as "the id" lives here, and the search box should
  // adapt to it: if the user already knew the FK target value, they wouldn't
  // need the picker.
  const pkColumn = useMemo<string | null>(() => {
    if (primaryKeyColumns.size !== 1) return null;
    return Array.from(primaryKeyColumns)[0] ?? null;
  }, [primaryKeyColumns]);

  // The "id-like" column we search against for the second OR branch and
  // surface in the placeholder. Prefers the PK; falls back to the FK target
  // when there's no single-column PK.
  const idColumn = pkColumn ?? effectiveColumn;

  const idColumnMeta = useMemo(
    () => parentColumns.find((c) => c.name === idColumn),
    [parentColumns, idColumn],
  );
  const idCategory = idColumnMeta
    ? categorizeDataType(idColumnMeta.dataType)
    : 'unknown';

  // Base filters supplied by the inline FilterWizard. AND-joined with the
  // main search filter (added per-query) and any caller-supplied
  // `targetFilter`.
  const baseFilters = wizardFilters;

  const trimmedSearch = debouncedSearch.trim();

  // Query A — runs in two situations:
  //   (a) the search box is empty (it returns rows matching the column-header
  //       filters, or all rows if those are also empty); or
  //   (b) the search box is non-empty AND a display column exists, in which
  //       case the search is added as a CONTAINS filter on that column.
  // When the user is searching but there's no display column to search by,
  // query A is skipped entirely — otherwise it would return the first 100
  // rows of the table regardless of the search term, which is misleading.
  // In that "no display column + search" case only query B (FK column search)
  // runs and the user can still find rows by ID.
  const wantDisplaySearch = !!trimmedSearch && !!displayColumn;
  const queryAEnabled =
    open && targetResolved && !metadataLoading && (!trimmedSearch || wantDisplaySearch) && !relationshipPort;

  // Query B — search by the parent's id column (PK if available, otherwise
  // the FK target). Only meaningful when the search box is populated AND the
  // id column is different from the display column. For numeric id columns
  // we use `=` (LIKE on a number doesn't work in MSSQL/PG without an explicit
  // cast); for string ids we use CONTAINS; for booleans we don't run a B
  // query (the search box doesn't make sense there).
  const idSearchCondition = useMemo<FilterCondition | null>(() => {
    if (!trimmedSearch) return null;
    if (idColumn === displayColumn) return null;
    if (idCategory === 'number') {
      const num = Number(trimmedSearch);
      if (Number.isNaN(num)) return null;
      return { operator: '=', value: num };
    }
    if (idCategory === 'boolean') return null;
    return { operator: 'CONTAINS', value: trimmedSearch };
  }, [trimmedSearch, idColumn, displayColumn, idCategory]);

  const queryA = useQuery({
    queryKey: [
      'fk-picker-A',
      connectionId,
      database,
      effectiveSchema,
      effectiveTable,
      JSON.stringify(baseFilters),
      displayColumn,
      trimmedSearch,
      effectiveTargetFilter,
      pickerSort?.field ?? null,
      pickerSort?.direction ?? null,
    ],
    queryFn: async () => {
      const filters: FilterGroup[] = wantDisplaySearch
        ? [
            ...baseFilters,
            {
              field: displayColumn!,
              conditions: [{ operator: 'CONTAINS', value: trimmedSearch }],
            },
            ...(effectiveTargetFilter ?? []),
          ]
        : [...baseFilters, ...(effectiveTargetFilter ?? [])];
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: createExecutionId(`fk-picker-${effectiveTable}-A`),
        table: { database, schema: effectiveSchema, tableName: effectiveTable },
        columns: '*',
        limit: PICKER_ROW_LIMIT,
        filters,
        sorting: sortingForQuery,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to load rows');
      }
      return {
        rows: result.rows ?? [],
        totalRowCount: result.totalRowCount,
      };
    },
    enabled: queryAEnabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const queryB = useQuery({
    queryKey: [
      'fk-picker-B',
      connectionId,
      database,
      effectiveSchema,
      effectiveTable,
      JSON.stringify(baseFilters),
      idColumn,
      trimmedSearch,
      idSearchCondition?.operator,
      effectiveTargetFilter,
      pickerSort?.field ?? null,
      pickerSort?.direction ?? null,
    ],
    queryFn: async () => {
      const filters: FilterGroup[] = idSearchCondition
        ? [...baseFilters, { field: idColumn, conditions: [idSearchCondition] }, ...(effectiveTargetFilter ?? [])]
        : [...baseFilters, ...(effectiveTargetFilter ?? [])];
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: createExecutionId(`fk-picker-${effectiveTable}-B`),
        table: { database, schema: effectiveSchema, tableName: effectiveTable },
        columns: '*',
        limit: PICKER_ROW_LIMIT,
        filters,
        sorting: sortingForQuery,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to load rows');
      }
      return {
        rows: result.rows ?? [],
        totalRowCount: result.totalRowCount,
      };
    },
    enabled: open && targetResolved && !metadataLoading && !!idSearchCondition && !relationshipPort,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Merge query A and query B rows, dedup by FK value, cap at PICKER_ROW_LIMIT.
  // A host port's page is searched / sorted locally instead (M7).
  const mergedRows = useMemo<Record<string, unknown>[]>(() => {
    if (relationshipPort) {
      return searchHostPickerRows({
        rows: host.rows ?? [],
        search: trimmedSearch,
        searchColumns: [displayColumn, idColumn],
        sort: pickerSort,
        limit: PICKER_ROW_LIMIT,
      });
    }
    const seen = new Set<string>();
    const out: Record<string, unknown>[] = [];
    const sources: Record<string, unknown>[][] = [];
    if (queryA.data?.rows) sources.push(queryA.data.rows);
    if (queryB.data?.rows) sources.push(queryB.data.rows);
    for (const src of sources) {
      for (const row of src) {
        const v = row[effectiveColumn];
        const key = v === null || v === undefined ? '__null__' : String(v);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(row);
        if (out.length >= PICKER_ROW_LIMIT) return out;
      }
    }
    return out;
  }, [queryA.data, queryB.data, effectiveColumn, relationshipPort, host.rows, trimmedSearch, displayColumn, idColumn, pickerSort]);

  const isLoading = relationshipPort
    ? metadataLoading || host.rowsLoading
    : metadataLoading ||
      queryA.isLoading ||
      (!!idSearchCondition && queryB.isLoading);
  const error = relationshipPort
    ? metadataError || host.rowsError
    : metadataError ||
      queryA.error ||
      (idSearchCondition ? queryB.error : null);

  // The picker shows every column from the parent table. We bias the layout
  // so the FK target comes first, the display column comes second, the
  // parent's id (PK) comes third when it differs from both, and the rest
  // follow in their original order — that way the value, the label, and the
  // id are always visible without horizontal scrolling.
  // Per-column metadata used by the header — json/enum category + FK status.
  // Keyed by lowercased column name so it tolerates the casing variance
  // between query results (mock fixtures use lowercase, real engines preserve
  // declared case). Real-FK callers without `engine` fall through to
  // 'not-json' / 'not-enum' so the glyphs simply don't render.
  const columnHeaderMeta = useMemo(() => {
    const m = new Map<
      string,
      {
        json: JsonColumnCategory;
        enumCat: EnumColumnCategory;
        isFk: boolean;
        isLooseFk: boolean;
        isPk: boolean;
      }
    >();
    for (const c of parentColumns) {
      const json: JsonColumnCategory = engine
        ? categorizeJsonColumn(
            {
              engine,
              schema: effectiveSchema,
              table: effectiveTable,
              name: c.name,
              dataType: c.dataType,
            },
            metaInfo ?? null,
          )
        : 'not-json';
      const enumCat: EnumColumnCategory = engine
        ? categorizeEnumColumn(
            {
              engine,
              schema: effectiveSchema,
              table: effectiveTable,
              name: c.name,
              dataType: c.dataType,
              isNativeEnumType: c.isNativeEnumType,
            },
            metaInfo ?? null,
          )
        : 'not-enum';
      m.set(c.name.toLowerCase(), {
        json,
        enumCat,
        isFk: c.isForeignKey,
        isLooseFk: !!c.isLoose,
        isPk: c.isPrimaryKey,
      });
    }
    return m;
  }, [parentColumns, engine, metaInfo, effectiveSchema, effectiveTable]);

  const orderedColumns = useMemo(() => {
    if (parentColumns.length === 0) return [];
    const head: string[] = [];
    const present = new Set(parentColumns.map((c) => c.name));
    if (present.has(effectiveColumn)) head.push(effectiveColumn);
    if (
      displayColumn &&
      displayColumn !== effectiveColumn &&
      present.has(displayColumn)
    ) {
      head.push(displayColumn);
    }
    if (
      pkColumn &&
      pkColumn !== effectiveColumn &&
      pkColumn !== displayColumn &&
      present.has(pkColumn)
    ) {
      head.push(pkColumn);
    }
    const rest = parentColumns
      .map((c) => c.name)
      .filter((n) => !head.includes(n));
    return [...head, ...rest];
  }, [parentColumns, effectiveColumn, displayColumn, pkColumn]);

  // Case-insensitive row-cell lookup. Real engines preserve metadata casing
  // in result rows, but some mock/fixture harnesses lowercase them. Exact
  // match wins; only fall through to a scan when it misses.
  const getCellValue = useCallback(
    (row: Record<string, unknown>, column: string): unknown => {
      if (column in row) return row[column];
      const lc = column.toLowerCase();
      for (const k of Object.keys(row)) {
        if (k.toLowerCase() === lc) return row[k];
      }
      return undefined;
    },
    [],
  );

  const handlePickRow = (row: Record<string, unknown>) => {
    const raw = getCellValue(row, effectiveColumn);
    if (raw === null || raw === undefined) return;
    // The resolved sourcePath flows back to the caller so loose-FK callsites
    // can stamp condition.sourcePath with the user's actual pick. Real-FK
    // callers (no activeLooseRel) get `undefined`, preserving the
    // pre-Pass-3 behavior.
    const resolvedSourcePath = activeLooseRel?.sourcePath || undefined;
    if (
      typeof raw === 'string' ||
      typeof raw === 'number' ||
      typeof raw === 'boolean' ||
      raw instanceof Date
    ) {
      onSelect(raw, resolvedSourcePath);
    } else {
      onSelect(JSON.stringify(raw), resolvedSourcePath);
    }
    onOpenChange(false);
  };

  const handleStartAddFilter = useCallback((columnName: string) => {
    setPendingDraftColumn(columnName);
  }, []);

  const handleClearPendingDraft = useCallback(() => {
    setPendingDraftColumn(null);
  }, []);

  const showInlineWizard = !relationshipPort && (wizardFilters.length > 0 || pendingDraftColumn !== null);

  const totalRowCount = queryA.data?.totalRowCount;
  const isCapped =
    typeof totalRowCount === 'number' && totalRowCount > PICKER_ROW_LIMIT;

  // Editing an existing loose-FK declaration — render the target picker with
  // the current target pre-filled. On save, drop back to the grid view; on
  // cancel, do the same. Placed before the `!targetResolved` branch so the
  // edit dialog short-circuits even though the target is currently resolved.
  if (targetResolved && isLoose && editingTarget && source && activeLooseRel) {
    return (
      <LooseRelationshipEditorDialog
        open={open}
        onOpenChange={onOpenChange}
        connectionId={connectionId}
        database={database}
        source={{
          schema: source.schema,
          table: source.table,
          name: source.column,
          dataType: source.dataType,
        }}
        allTables={allTables}
        engine={engine}
        metadataEditors={relationshipPort}
        existingRelationshipId={activeLooseRel.id}
        initialTarget={{
          schema: activeLooseRel.toSchema,
          table: activeLooseRel.toTable,
          column: activeLooseRel.toColumn,
        }}
        initialSourceFilter={activeLooseRel.sourceFilter}
        initialTargetFilter={activeLooseRel.targetFilter}
        initialSourcePath={activeLooseRel.sourcePath}
        onSaved={() => setEditingTarget(false)}
        onCancel={() => setEditingTarget(false)}
        onCloseAutoFocus={handleCloseAutoFocus}
      />
    );
  }

  // Multi-FK chooser — when the column has N > 1 loose declarations and the
  // caller didn't pass a `sourcePath`, ask the user which one to operate on.
  // The "+ Add another" footer falls through to the target picker so users
  // can declare an additional FK on the same column without leaving the dialog.
  if (needsChooser && source) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-md"
          data-testid="fk-lookup-picker-multi-chooser"
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <DialogHeader>
            <DialogTitle className="text-sm">
              Pick a relationship for{' '}
              <span className="font-mono">{source.column}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="text-[12px] text-muted-foreground mb-2">
            This column has multiple declared relationships. Pick the one
            you want to look up against, or add a new declaration.
          </div>
          <div
            className="flex flex-col gap-1 max-h-[280px] overflow-auto"
            data-testid="fk-lookup-picker-multi-chooser-list"
          >
            {looseRels.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setChosenLooseRelId(r.id)}
                className="flex items-center justify-between text-xs border rounded p-2 hover:bg-accent text-left"
                data-testid={`fk-lookup-picker-multi-chooser-row-${r.id}`}
              >
                <span className="font-mono">
                  {r.sourcePath ? (
                    <>
                      {r.sourcePath} →{' '}
                    </>
                  ) : (
                    <>(whole column) → </>
                  )}
                  {qualifiedLabel(r.toSchema, r.toTable, r.toColumn)}
                </span>
                <ArrowRight className="h-3 w-3 opacity-60" />
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddingAnother(true)}
              data-testid="fk-lookup-picker-multi-chooser-add"
            >
              + Add another
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Adding a new declaration on top of an existing multi-FK column. The
  // chooser footer routes here so the user can declare another path-keyed
  // FK without first removing an existing one.
  if (addingAnother && source) {
    return (
      <LooseRelationshipEditorDialog
        open={open}
        onOpenChange={onOpenChange}
        connectionId={connectionId}
        database={database}
        source={{
          schema: source.schema,
          table: source.table,
          name: source.column,
          dataType: source.dataType,
        }}
        allTables={allTables}
        engine={engine}
        metadataEditors={relationshipPort}
        // Pass 3 Slice C: pre-fill the source-path row when the
        // caller arrived via a *_lookup sub-op with a path bound.
        initialSourcePath={sourcePath}
        onSaved={(rel) => {
          setAddingAnother(false);
          setChosenLooseRelId(rel.id);
        }}
        onCancel={() => setAddingAnother(false)}
        onCloseAutoFocus={handleCloseAutoFocus}
      />
    );
  }

  // No declared target yet — render the loose-FK target selection UI instead
  // of the row grid. Once the user saves, MetaInfo invalidates and the
  // parent re-renders this picker with a real target on the next pass.
  if (!targetResolved) {
    if (!source) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-md" onCloseAutoFocus={handleCloseAutoFocus}>
            <DialogHeader>
              <DialogTitle>No related table</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              This column has no declared relationship and no source context
              was provided.
            </p>
          </DialogContent>
        </Dialog>
      );
    }
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl"
          data-testid="fk-lookup-picker-unresolved"
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <DialogHeader>
            <DialogTitle className="text-sm">
              Pick target table and column
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-teal-500/30 bg-teal-500/5 px-3 py-2 text-[12px] text-foreground/80 mb-2">
            <div className="font-medium text-foreground mb-0.5">
              This column isn&rsquo;t a foreign key.
            </div>
            <div className="text-muted-foreground">
              To look up related rows for{' '}
              <span className="font-mono">{source.column}</span>, choose the
              table and column it points to. Jam SQL Studio will save this as a{' '}
              <em>loose relationship</em> for the database, so future lookups
              work without recreating it.
            </div>
          </div>
          <LooseRelationshipTargetPicker
            connectionId={connectionId}
            database={database}
            source={{
              schema: source.schema,
              table: source.table,
              name: source.column,
              dataType: source.dataType,
            }}
            allTables={allTables}
            engine={engine}
            metadataEditors={relationshipPort}
            // Pass 3 Slice C: when the caller arrived with a path
            // pre-bound (e.g. property_lookup user typed `$.customer_id`
            // before clicking "Pick…"), pre-fill the source-path row so
            // they don't retype it.
            initialSourcePath={sourcePath}
            onSaved={() => {
              // Parent re-renders with merged FK metadata via useTableMetadata
              // + useMetaInfo. The picker stays open so the user lands on the
              // grid next render and can pick a row.
            }}
            onCancel={() => onOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl h-[80vh] flex flex-col p-0 overflow-hidden"
        data-testid="fk-lookup-picker"
        // F1-#2 — Escape-in-popover seam consolidation, extended to this
        // Dialog host. The inline `FilterWizard` below mounts a committed
        // `FilterChip` whose in-place value/JSON-draft/OR-draft edits claim
        // Escape for themselves via `escapeClaimProps` on the chip's own
        // root while editing; without this ancestor check Radix's
        // document-capture dismiss listener would win the race and close
        // the whole picker instead of just cancelling the edit (see
        // `escapeClaimTarget.ts`'s docblock for why a per-input
        // `preventDefault()` can never win that race on its own).
        onEscapeKeyDown={(e) => {
          if (isEscapeClaimTarget(e.target)) e.preventDefault();
        }}
        // Land initial focus on the search input at mount time instead of
        // Radix's default "first tabbable" (the loose-FK bar's edit button
        // when it renders) — synchronous, so it can never steal focus from
        // something the user opened after the dialog appeared.
        onOpenAutoFocus={(e) => {
          const input = searchInputRef.current;
          if (!input) return;
          e.preventDefault();
          input.focus();
        }}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
      {/* Disable portalling for every popover rendered inside this dialog.
          The modal Dialog sets `pointer-events: none` on <body>, so a
          body-level popover portal would inherit the lock — clicks would
          fall through to the picker row beneath. Inline-rendering keeps the
          popover inside the dialog's `pointer-events: auto` subtree. */}
      <PopoverPortalDefaultProvider portalled={false}>
        <DialogHeader className="px-4 py-3 border-b border-border bg-muted/30">
          {crumbs && crumbs.length > 0 && (
            <div
              className="flex items-center flex-wrap gap-1 text-[11px] text-muted-foreground mb-1"
              data-testid="fk-lookup-picker-breadcrumb"
            >
              {crumbs.map((crumb, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                  <span className="font-mono">{crumb.fromTable}</span>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="font-mono">{crumb.fromColumn}</span>
                  <ChevronRight className="h-3 w-3 opacity-60" />
                </span>
              ))}
              <span className="font-mono text-foreground">{fullTableName}</span>
            </div>
          )}
          <DialogTitle className="text-sm">
            {sourceLabel ? `Pick ${sourceLabel} from ` : 'Pick a row from '}
            <span className="font-mono">{fullTableName}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-0.5">
            Search matches{' '}
            {displayColumn ? (
              <>
                <span className="font-mono">{displayColumn}</span> and{' '}
              </>
            ) : null}
            <span className="font-mono">{idColumn}</span>. Returns{' '}
            <span className="font-mono">{effectiveColumn}</span>.
          </div>
        </DialogHeader>

        {/* Loose-FK collapsed bar — only when this column's target is a
            confirmed loose-FK and we're not currently editing it. Real-FK
            columns deliberately don't show this bar. */}
        {isLoose && !editingTarget && activeLooseRel && (
          <div
            className="flex items-center gap-1 px-3 py-1 text-[11px] border-b border-border bg-muted/30"
            data-testid="loose-fk-collapsed-bar"
          >
            <Link2 className="h-3 w-3 text-teal-500 opacity-70" aria-hidden />
            <span
              className="text-muted-foreground"
              data-testid="loose-fk-collapsed-target"
            >
              {activeLooseRel.sourcePath ? (
                <>
                  <span className="font-mono">{activeLooseRel.sourcePath}</span> →{' '}
                </>
              ) : (
                '→ '
              )}
              {qualifiedLabel(activeLooseRel.toSchema, activeLooseRel.toTable, activeLooseRel.toColumn)}
            </span>
            <button
              type="button"
              onClick={() => setEditingTarget(true)}
              className="ml-auto p-1 rounded hover:bg-accent"
              aria-label="Edit relationship"
              title="Edit relationship"
              data-testid="loose-fk-edit-btn"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onRemoveLoose}
              className="p-1 rounded hover:bg-destructive/10 text-destructive"
              aria-label="Remove relationship"
              title="Remove relationship"
              data-testid="loose-fk-remove-btn"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Main search + Add Filter toolbar */}
        <div className="px-4 py-2 border-b border-border flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                displayColumn
                  ? `Search ${displayColumn} or ${idColumn}...`
                  : `Search ${idColumn}...`
              }
              className="h-8 pl-7 pr-7 text-xs"
              data-testid="fk-lookup-picker-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* "Add Filter" toolbar button — mirrors `toolbar-add-filter` on
              the main Table Explorer toolbar. Always mounted so the Radix
              Popover lifecycle stays stable across picker re-renders (the
              picker re-renders as background queries settle, which would
              otherwise detach an opening popover's items mid-click).
              Visually hidden when the inline FilterWizard is mounted — the
              wizard's own header carries the next Add Filter entry point. */}
          <div className={showInlineWizard || relationshipPort ? 'hidden' : 'contents'}>
            <AddFilterToolbarButton
              columns={parentColumns}
              onSelect={handleStartAddFilter}
            />
          </div>
        </div>

        {/* Inline FilterWizard — mounted when there are committed filters
            or a pending draft column from the toolbar Add Filter button.
            The wizard renders its own filter chips, "Add Filter" button
            (for subsequent filters), peek-matching-rows popover, and
            clear-all. FK drill-down on a chip is handled by
            FilterChip → FkLookupValueButton → FkLookupPicker (recursion
            via this same component). */}
        {showInlineWizard && (
          <FilterWizard
            filters={wizardFilters}
            columns={parentColumns}
            onFiltersChange={setWizardFilters}
            initialDraftColumn={pendingDraftColumn}
            onDraftHandled={handleClearPendingDraft}
            connectionId={connectionId}
            database={database}
            tableSchema={effectiveSchema}
            tableName={effectiveTable}
            engine={engine}
          />
        )}

        {/* Results — stable height (the dialog is `h-[80vh]`, this area is
            `flex-1 min-h-0`). A loading overlay carries the centered spinner
            on top of skeleton or real rows so the grid never collapses to
            content-height between refetches. */}
        <div className="flex-1 overflow-auto min-h-0 relative">
          {isLoading && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-[1px] pointer-events-none"
              data-testid="fk-lookup-picker-loading-overlay"
            >
              <div className="flex flex-col items-center gap-2">
                <RandomDotmSpinner size={28} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading rows…</span>
              </div>
            </div>
          )}

          {!isLoading && error && (
            <div className="px-4 py-6 text-xs text-destructive">
              Failed to load: {error instanceof Error ? error.message : String(error)}
            </div>
          )}

          {!error && orderedColumns.length === 0 && isLoading && (
            <SkeletonRows />
          )}

          {!error && orderedColumns.length > 0 && (
            <table
              className="w-full text-xs border-collapse"
              data-testid="fk-lookup-picker-grid"
            >
              <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
                <tr>
                  {orderedColumns.map((col) => {
                    const lower = col.toLowerCase();
                    const isFkTargetCol = lower === effectiveColumn.toLowerCase();
                    const isDisplayCol =
                      !!displayColumn && lower === displayColumn.toLowerCase();
                    const meta = columnHeaderMeta.get(lower);
                    const isPkCol = !!meta?.isPk;
                    const isFkCol = !!meta?.isFk;
                    const isLooseFkCol = !!meta?.isLooseFk;
                    // Drop the eligible (`?`) state — the soft "this could be
                    // an enum/JSON column" hint clutters every short-text or
                    // hinted-int column in the picker. Declared (user opt-in)
                    // and native (engine type) remain because they're strong
                    // signals worth surfacing inline.
                    const jsonState =
                      meta?.json === 'declared-json'
                        ? 'declared'
                        : meta?.json === 'native-json'
                          ? 'native'
                          : null;
                    const enumState =
                      meta?.enumCat === 'declared-enum'
                        ? 'declared'
                        : meta?.enumCat === 'native-enum'
                          ? 'native'
                          : null;
                    const sortDir =
                      pickerSort?.field === col ? pickerSort.direction : null;
                    return (
                      <th
                        key={col}
                        className={`group/header px-2 py-1.5 text-left font-medium border-b border-border whitespace-nowrap select-none cursor-pointer hover:bg-accent/40 ${
                          isFkTargetCol
                            ? 'text-blue-600 dark:text-blue-400'
                            : isDisplayCol
                              ? 'text-foreground'
                              : isPkCol
                                ? 'text-warning'
                                : 'text-muted-foreground'
                        }`}
                        title={
                          isFkTargetCol
                            ? `Sort by ${col} (FK target — value returned)`
                            : isDisplayCol
                              ? `Sort by ${col} (display column)`
                              : isPkCol
                                ? `Sort by ${col} (primary key)`
                                : `Sort by ${col}`
                        }
                        onClick={() => cycleSort(col)}
                        data-testid={`fk-lookup-picker-header-${col}`}
                      >
                        <div className="flex items-center gap-1">
                          {isPkCol && (
                            <abbr
                              className="text-warning text-[10px] no-underline"
                              title="Primary Key"
                              aria-label="Primary Key"
                            >
                              PK
                            </abbr>
                          )}
                          {isFkCol &&
                            (isLooseFkCol ? (
                              <abbr
                                className="text-teal-500 text-[10px] italic no-underline"
                                title="Loose Foreign Key (user-declared)"
                                aria-label="Loose Foreign Key"
                                data-testid="fk-lookup-picker-header-loose-fk-prefix"
                              >
                                FK
                              </abbr>
                            ) : (
                              <abbr
                                className="text-blue-500 text-[10px] no-underline"
                                title="Foreign Key"
                                aria-label="Foreign Key"
                                data-testid="fk-lookup-picker-header-fk-prefix"
                              >
                                FK
                              </abbr>
                            ))}
                          {jsonState && (
                            <JsonHeaderGlyph state={jsonState} onClick={() => {}} />
                          )}
                          {enumState && (
                            <EnumHeaderGlyph state={enumState} onClick={() => {}} />
                          )}
                          <span>{col}</span>
                          {sortDir === 'asc' && (
                            <ArrowUp
                              className="h-3 w-3 text-foreground/80"
                              data-testid={`fk-lookup-picker-header-sort-asc-${col}`}
                              aria-hidden
                            />
                          )}
                          {sortDir === 'desc' && (
                            <ArrowDown
                              className="h-3 w-3 text-foreground/80"
                              data-testid={`fk-lookup-picker-header-sort-desc-${col}`}
                              aria-hidden
                            />
                          )}
                          {!relationshipPort && <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartAddFilter(col);
                            }}
                            className="ml-auto p-0.5 rounded opacity-40 hover:opacity-100 hover:bg-accent group-hover/header:opacity-80 transition-opacity"
                            title={`Filter by ${col}`}
                            aria-label={`Filter by ${col}`}
                            data-testid={`fk-lookup-picker-header-filter-${col}`}
                          >
                            <Filter className="h-3 w-3" aria-hidden />
                          </button>}
                        </div>
                      </th>
                    );
                  })}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {mergedRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={orderedColumns.length + 1}
                      className="px-4 py-8 text-center text-xs text-muted-foreground"
                    >
                      {trimmedSearch || wizardFilters.length > 0
                        ? 'No rows match your filters.'
                        : 'No rows in this table.'}
                    </td>
                  </tr>
                )}
                {mergedRows.map((row, idx) => {
                  const rawKey = getCellValue(row, effectiveColumn);
                  const rowKey =
                    rawKey === null || rawKey === undefined
                      ? null
                      : String(rawKey);
                  const isSelected =
                    rowKey !== null && rowKey === selectedRowKey;
                  return (
                  <tr
                    key={idx}
                    onClick={() => {
                      if (rowKey === null) return;
                      setSelectedRowKey(rowKey);
                    }}
                    onDoubleClick={() => handlePickRow(row)}
                    className={`cursor-pointer group ${
                      isSelected
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/60'
                    }`}
                    data-testid="fk-lookup-picker-row"
                    data-selected={isSelected ? 'true' : 'false'}
                    title="Click to select; double-click or use Apply to commit"
                  >
                    {orderedColumns.map((col) => {
                      const cellValue = row[col];
                      const isFkCol =
                        col.toLowerCase() === effectiveColumn.toLowerCase();
                      const display =
                        cellValue === null || cellValue === undefined
                          ? 'NULL'
                          : typeof cellValue === 'object'
                            ? JSON.stringify(cellValue)
                            : String(cellValue);
                      return (
                        <td
                          key={col}
                          className={`px-2 py-1 border-b border-border/50 truncate max-w-[260px] ${
                            isFkCol ? 'font-mono text-blue-600 dark:text-blue-400' : ''
                          } ${
                            cellValue === null || cellValue === undefined
                              ? 'text-muted-foreground italic'
                              : ''
                          }`}
                          title={display}
                        >
                          {display}
                        </td>
                      );
                    })}
                    <td className="px-1 py-1 border-b border-border/50 text-muted-foreground/60 group-hover:text-foreground">
                      <ArrowRight className="h-3 w-3" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between">
          <div className="text-[11px] text-muted-foreground">
            {!isLoading && !error && (
              <>
                {mergedRows.length} row{mergedRows.length === 1 ? '' : 's'}
                {(isCapped || mergedRows.length >= PICKER_ROW_LIMIT) &&
                  ` (capped at ${PICKER_ROW_LIMIT}; refine search or filters)`}
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onClear && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClear();
                  onOpenChange(false);
                }}
                className="h-7 text-xs"
                data-testid="fk-lookup-picker-clear"
              >
                Clear
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-7 text-xs"
              data-testid="fk-lookup-picker-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={selectedRowKey === null}
              onClick={() => {
                if (selectedRowKey === null) return;
                const row = mergedRows.find((r) => {
                  const v = getCellValue(r, effectiveColumn);
                  return v !== null && v !== undefined && String(v) === selectedRowKey;
                });
                if (row) handlePickRow(row);
              }}
              className="h-7 text-xs"
              title={
                selectedRowKey === null
                  ? 'Select a row first'
                  : 'Apply selected row'
              }
              data-testid="fk-lookup-picker-apply"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverPortalDefaultProvider>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Skeleton rows shown while initial metadata is loading (no `orderedColumns`
 * yet). Renders a fixed-height row strip so the dialog body fills its
 * `h-[80vh]` container instead of collapsing to content-height while
 * `useTableMetadata` is still in flight.
 */
function SkeletonRows() {
  const rowCount = 14;
  return (
    <div className="p-2" data-testid="fk-lookup-picker-skeleton">
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: rowCount }).map((_, i) => (
          <div
            key={i}
            className="h-5 rounded bg-muted/60 animate-pulse"
            style={{ width: `${60 + ((i * 37) % 35)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Toolbar "Add Filter" button rendered to the right of the search bar. Mirrors
 * `AddColumnButton(type='filter')` from TableExplorerToolbar but inlined here
 * to keep the picker self-contained — no shared dependency on the toolbar
 * module. Picking a column triggers `onSelect(columnName)`, which the picker
 * passes through to FilterWizard's `initialDraftColumn` to mount the wizard
 * with an empty draft on that column.
 */
type AddFilterToolbarButtonProps = {
  columns: TableExplorerColumn[];
  onSelect: (columnName: string) => void;
  disabled?: boolean;
};

function AddFilterToolbarButton({
  columns,
  onSelect,
  disabled = false,
}: AddFilterToolbarButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredColumns = useMemo(() => {
    const q = columnSearch.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) =>
      normalizeColumnName(c.name).includes(normalizeColumnName(q)),
    );
  }, [columns, columnSearch]);

  useEffect(() => {
    if (isOpen) {
      setColumnSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [columnSearch]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (name: string) => {
      onSelect(name);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredColumns.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((p) =>
            p < filteredColumns.length - 1 ? p + 1 : p,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((p) => (p > 0 ? p - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredColumns.length) {
            handleSelect(filteredColumns[selectedIndex].name);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [filteredColumns, selectedIndex, handleSelect],
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs shrink-0"
          disabled={disabled || columns.length === 0}
          data-testid="fk-lookup-picker-add-filter"
        >
          <Filter className="h-3.5 w-3.5 mr-1" />
          Add Filter
          <Plus className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </PopoverTrigger>
      {/* portalled={false} keeps the popover inside the picker's
          DialogContent so it stacks above the dialog overlay. With a
          default portal the popover lands at document.body and is
          intercepted by the overlay's pointer-events. */}
      <PopoverContent className="w-56 p-2" align="end" portalled={false}>
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-muted-foreground">
            Select column to filter
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search columns..."
              className="h-7 pl-7 text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto" ref={listRef}>
            {filteredColumns.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2 text-center">
                {columns.length === 0
                  ? 'No columns available'
                  : 'No matching columns'}
              </div>
            ) : (
              filteredColumns.map((col, index) => (
                <button
                  key={col.name}
                  data-index={index}
                  onClick={() => handleSelect(col.name)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded cursor-pointer hover:bg-accent ${
                    index === selectedIndex ? 'bg-accent' : ''
                  }`}
                  data-testid={`fk-lookup-picker-add-filter-col-${col.name}`}
                >
                  <span className="font-medium">{col.name}</span>
                  <span className="text-muted-foreground text-[10px]">
                    {col.dataType}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
